import React from 'react';
import { ThunkAction } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { ActivateSubscriptionModalNew } from '@src/containers/ActivateSubscriptionModalNew';
import { RootState } from '@src/redux/interfaces/redux.interface';
import { setModalContent } from '@src/redux/actions/modal';
import apolloClient from '@src/graphql/apollo';
import { INACTIVE_SUBSCRIPTION_STATES, LOCKED_STATES } from '@src/constants';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { CustomerDocument, CustomerQuery } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { ActivateFreemiumModal } from '@src/containers/ActivateFreemiumModal';
import { OpenActivateSubscriptionModalParams } from './interfaces/openActivateSubscriptionModal.interface';
import { CheckSubscriptionStateParams } from './interfaces/checkSubscriptionState.interface';
import { isSubscriptionPlanType } from './typeGuards/isSubscriptionPlanType';

export const openActivateSubscriptionModal = ({ onCompleted }: OpenActivateSubscriptionModalParams = {}): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async dispatch => {
    dispatch(
      setModalContent({
        config: {
          hideCloseButton: true,
          shouldCloseOnEsc: true,
          withoutPadding: true,
        },
        content: <ActivateSubscriptionModalNew onCompleted={onCompleted} />,
      }),
    );
  };
};

export const openActivateFreemiumModal = ({ onCompleted }: OpenActivateSubscriptionModalParams = {}): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async dispatch => {
    dispatch(
      setModalContent({
        config: {
          hideCloseButton: true,
          shouldCloseOnEsc: true,
          withoutPadding: true,
        },
        content: <ActivateFreemiumModal onCompleted={onCompleted} />,
      }),
    );
  };
};

/**
 * checks if trial is ended or subscription state is inactive, opens paywall depending on that
 * executes one from callback functions
 */
export const checkSubscriptionState = (
  { nextFunction, metricsPrevContext, eventSource, onCompleted, isNetworkOnly }: CheckSubscriptionStateParams,
  shouldShowOnTrial?: boolean,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async dispatch => {
    try {
      const { data } = await apolloClient.query<CustomerQuery>({
        fetchPolicy: isNetworkOnly ? 'network-only' : 'cache-first',
        query: CustomerDocument,
      });

      const selectedStates = shouldShowOnTrial ? LOCKED_STATES : INACTIVE_SUBSCRIPTION_STATES;

      const planInfo = data?.customer.planInfo;

      if (isSubscriptionPlanType(planInfo) && planInfo.state && selectedStates.includes(planInfo.state)) {
        dispatch(openActivateSubscriptionModal({ onCompleted }));

        trackEvent({
          context: MetricContext.ACTIVATE_SUBSCRIPTION,
          interaction: MetricAction.OPEN_ACTIVATE_SUBSCRIPTION,
          payload: {
            'Event Source': eventSource,
            'Previous Context': metricsPrevContext,
          },
        });
      } else {
        nextFunction && nextFunction();
      }
    } catch (e) {
      dispatch(setErrorFlashMessage());
    }
  };
};
